import React, { useEffect, useState } from "react"
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  LinearProgress,
  CircularProgress,
  FormHelperText,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import UploadIcon from "@mui/icons-material/CloudUpload"
import { TrashIcon } from "../../icons"
import { fileUpload, fileDelete } from "../../api/uploadApi"
import { fetchCourseNames } from "../../api/courseApi"
import ValidationErrorText from "../common/ValidationErrorText"

const initialForm = {
  studentName: "",
  courseName: "",
  testimonial: "",
  rating: 5,
  image: "", // Only store URL
}

const ratingOptions = [2.5, 3, 3.5, 4, 4.5, 5]

export default function StudentSpotlight({ label, value = [], onChange, readOnly = false,fieldType,fieldErrors }) {
  const forms = value
  const [courseOptions, setCourseOptions] = useState([])

  useEffect(() => {
    fetchCourseNames(setCourseOptions)
  }, [])

  // For upload progress and errors per index
  const [uploadingIdx, setUploadingIdx] = useState(null)
  const [progress, setProgress] = useState(0)
  const [uploadError, setUploadError] = useState("")

  const updateForm = (idx, changes) => {
    const newForms = forms.map((f, i) =>
      i === idx ? { ...f, ...changes } : f
    )
    onChange && onChange(newForms)
  }

  const handleAddMore = () => {
    onChange && onChange([...forms, { ...initialForm }])
  }

  const handleRemove = (idx) => {
    onChange && onChange(forms.filter((_, i) => i !== idx))
  }

  const handleChange = (idx, field) => (e) => {
    let value = e.target.value
    if (field === "rating") {
      value = value ? parseFloat(value) : 5
    }
    updateForm(idx, { [field]: e.target.value })
  }

  // Upload handler: on upload, only set image: url
  const handleImageChange = (idx) => async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Only accept jpg/png
    const isValidFormat = ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)
    const isValidSize = file.size <= 2 * 1024 * 1024
    if (!isValidFormat) {
      setUploadError("Invalid file format")
      return
    }
    if (!isValidSize) {
      setUploadError("File too large (max 2MB)")
      return
    }

    setUploadError("")
    setUploadingIdx(idx)
    setProgress(0)

    try {
      const data = await fileUpload(file, (prog) => setProgress(prog))
      updateForm(idx, { image: data.url || "" })
    } catch {
      setUploadError("Image upload failed!")
    } finally {
      setUploadingIdx(null)
      setProgress(0)
    }
  }

  // Remove handler (delete from backend if uploaded, then local state)
  const handleRemoveImage = (idx) => async (e) => {
    e?.stopPropagation && e.stopPropagation()
    const { image } = forms[idx]
    if (image) {
      try {
        await fileDelete(image)
      } catch {
        setUploadError("Failed to delete image from server")
        return
      }
    }
    updateForm(idx, { image: "" })
  }

  const inputStyle = {
    height: 38,
    fontSize: 13,
    '& input': {
      height: '100%',
      padding: '0 14px',
      fontSize: 13,
    },
  }
  const labelStyle = { fontSize: 13, fontWeight: 500, marginBottom: "4px" }

  return (
    <Box>
      <Typography fontSize="14px" fontWeight="500" mb={1}>
        {label}
      </Typography>

      {forms.map((form, idx) => (
        <Box
          key={idx}
          sx={{
            borderRadius: 2,
            mb: 3,
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          {/* Image Upload UI */}
          <Box>
          <Box
            sx={{
              width: { xs: 120, sm: 150, md: 140, lg: 140, xl: 180 },
              height: { xs: 120, sm: 150, md: 140, lg: 140, xl: 180 },
              border: "2px dashed #7b9fe0",
              borderRadius: 2,
              position: "relative",
              cursor: readOnly ? "not-allowed" : "pointer",
              overflow: "hidden",
              flexShrink: 0,
              background: uploadingIdx === idx ? "#f4f4f4" : undefined,
            }}
            onClick={() =>
              !readOnly && uploadingIdx !== idx && document.getElementById(`student-image-upload-${idx}`).click()
            }
          >
            {uploadingIdx === idx ? (
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress size={28} />
                <Typography fontSize="13px" mt={1}>Uploading...</Typography>
                <Box sx={{ width: "80%", mt: 1 }}>
                  <LinearProgress value={progress} variant="determinate" sx={{ height: 6, borderRadius: 2 }} />
                </Box>
              </Box>
            ) : form.image ? (
              <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
                <img
                  src={form.image}
                  alt="student"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                {/* Remove Icon */}
                {!readOnly && (
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage(idx)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255,255,255,0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,1)",
                      },
                    }}
                    disabled={uploadingIdx === idx}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                {/* View Uploaded Link */}
                {form.image && (
                  <a
                    href={form.image}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      position: "absolute",
                      left: 8, bottom: 8, fontSize: 11,
                      color: "#1075FF", background: "#fff", borderRadius: 4, padding: "2px 8px", textDecoration: "none"
                    }}
                    onClick={e => e.stopPropagation()}
                  >View Uploaded</a>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "#2c3e9e",
                  fontWeight: "bold",
                  px: 1,
                  textAlign: "center",
                  fontSize: 14,
                  lineHeight: 1.3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.5,
                    color: "#363853",
                    fontWeight: "600",
                    fontSize: "12px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <UploadIcon sx={{ fontSize: 24, fill: "#1075FF" }} />
                  Upload Student Profile image
                </Typography>
                <Typography
                  sx={{
                    mt: 1.5,
                    color: "#363853",
                    fontWeight: "500",
                    fontSize: "10px",
                  }}
                >
                  Upload a high-quality JPG/PNG, min 300x300 px, max 2MB.
                </Typography>
              </Box>
            )}
            <input
              id={`student-image-upload-${idx}`}
              type="file"
              accept="image/png, image/jpeg"
              style={{ display: "none" }}
              disabled={readOnly || uploadingIdx === idx}
              onChange={handleImageChange(idx)}
            />
            {uploadingIdx === idx && uploadError && (
              <Typography color="error" fontSize={12} mt={1} sx={{ position: "absolute", bottom: 6, left: 6, right: 6 }}>
                {uploadError}
              </Typography>
            )}
          </Box>
          <ValidationErrorText error={fieldErrors?.[`${fieldType}[${idx}].image`]}/>
          </Box>
          <Grid container spacing={2} flex={1}>
            <Grid xs={12} sm={6}>
              <Typography sx={labelStyle} mb={0.5}>Student Name</Typography>
              <TextField
                fullWidth
                size="small"
                value={form.studentName}
                onChange={handleChange(idx, "studentName")}
                placeholder="Enter Student Name"
                InputProps={{ sx: inputStyle, readOnly }}
                disabled={readOnly}
              />
          <ValidationErrorText error={fieldErrors?.[`${fieldType}[${idx}].studentName`]}/>
            </Grid>
            <Grid xs={12} sm={6}>
              <Typography sx={labelStyle} mb={0.5}>Course Name</Typography>
              <TextField
                fullWidth
                size="small"
                select
                value={form.courseName}
                onChange={handleChange(idx, "courseName")}
                InputProps={{ sx: inputStyle, readOnly }}
                disabled={readOnly}
              >
                {courseOptions.map((option) => (
                  <MenuItem key={option} value={option.label} sx={{ fontSize: "13px" }}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
          <ValidationErrorText error={fieldErrors?.[`${fieldType}[${idx}].courseName`]}/>
            </Grid>
            <Grid xs={12}>
              <Typography sx={labelStyle} mb={0.5}>Testimonial</Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                value={form.testimonial}
                onChange={handleChange(idx, "testimonial")}
                placeholder="Enter Testimonial"
                sx={{
                  "& .MuiInputBase-inputMultiline": {
                    fontSize: "13px",
                    padding: "0px 8px",
                    "&::placeholder": {
                      fontSize: "13px !important",
                      color: "#999",
                    },
                  },
                }}
                InputProps={{ readOnly }}
                disabled={readOnly}
              />
          <ValidationErrorText error={fieldErrors?.[`${fieldType}[${idx}].testimonial`]}/>
            </Grid>
            <Grid xs={12} sm={6}>
              <Typography sx={labelStyle} mb={0.5}>Rating</Typography>
              <TextField
                fullWidth
                select
                size="small"
                value={form.rating}
                onChange={handleChange(idx, "rating")}
                InputProps={{ sx: inputStyle, readOnly }}
                SelectProps={{ displayEmpty: true }}
                disabled={readOnly}
              >
                <MenuItem value="" disabled sx={{ fontSize: "13px", color: "#999" }}>
                  Select Rating
                </MenuItem>
                {ratingOptions.map((r) => (
                  <MenuItem key={r} value={Number(r)} sx={{ fontSize: "13px" }}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
          <ValidationErrorText error={fieldErrors?.[`${fieldType}[${idx}].rating`]}/>
            </Grid>
            {/* Remove button: hidden for first 3 */}
            {idx >= 3 && !readOnly && (
              <Grid xs={12}>
                <Button
                  variant="contained"
                  sx={{
                    background: '#FF4141',
                    color: '#fff',
                    textTransform: "capitalize",
                    fontWeight: '400',
                  }}
                  startIcon={<TrashIcon style={{ fill: '#fff' }} />}
                  onClick={() => handleRemove(idx)}
                >
                  Remove
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}
      {/* Add More Button */}
      {!readOnly && (
        <Button
          sx={{ color: "#1075FF", border: "1px solid #1075FF", textTransform: "capitalize", px: 3, mt: 1 }}
          onClick={handleAddMore}
        >
          + Add More
        </Button>
      )}
    </Box>
  )
}